import type {
  DataFetchParams,
  DataFetchResponse,
  FilterOptionsParams,
} from '@/components/DataTable';
import {
  listUserGroupedDataTableAlerts,
  getUserAlertFilterOptions,
} from '@kirin/api-client/api/alerts';
import type {
  AlertsSortField,
  AlertVisibility,
  GroupedAlert,
  UserAlertsFilterOptionField,
} from '@kirin/api-client/contracts/alerts';
import { USER_ALERTS_FILTERABLE_FIELDS } from '@kirin/api-client/contracts/alerts';
import { apiClient } from '@/lib/api-client';

/**
 * Fetch user alerts with pagination for the DataTable.
 * Always returns the table engine response shape (GroupedAlertsTableResponse).
 *
 * `visibility` is the unified KIR-2512 selector that replaces the legacy
 * `includeMuted` / `includeExcluded` toggles.
 */
export async function fetchUserAlerts(
  params: DataFetchParams,
  signal?: AbortSignal,
  visibility?: AlertVisibility
): Promise<DataFetchResponse<GroupedAlert>> {
  const response = await listUserGroupedDataTableAlerts(
    apiClient,
    {
      page: params.pagination.pageIndex + 1,
      limit: params.pagination.pageSize,
      filters: params.filters,
      sortBy: params.sort?.field as AlertsSortField | undefined,
      sortOrder: params.sort?.order,
      search: params.search,
      visibility,
      knownTotal: params.knownTotal,
    },
    { signal }
  );

  return response;
}

/**
 * KIR-5098: the shared alert filter-options response may carry tagged items
 * (endpoints); these personal-alert fields only ever answer plain strings.
 */
function toStringOptions(options: Array<string | { value: string }>): string[] {
  return options.map(o => (typeof o === 'string' ? o : o.value));
}

/**
 * Fetch distinct alert type values for the type filter dropdown.
 */
export async function fetchUserAlertTypeOptions(
  params: FilterOptionsParams
): Promise<string[]> {
  const response = await getUserAlertFilterOptions(apiClient, {
    field: USER_ALERTS_FILTERABLE_FIELDS.TYPE as UserAlertsFilterOptionField,
    search: params.search,
    limit: params.limit,
    offset: params.offset,
  });

  return toStringOptions(response.options);
}

/**
 * KIR-3769: Fetch distinct composite title keys (`<subPolicyKey>::<type>`) for
 * the personal-alerts "Title" filter dropdown. Search is applied client-side
 * (the key is not the display label), so no `search` param is forwarded.
 */
export async function fetchUserAlertTitleOptions(
  params: FilterOptionsParams
): Promise<string[]> {
  const response = await getUserAlertFilterOptions(apiClient, {
    field: USER_ALERTS_FILTERABLE_FIELDS.TITLE as UserAlertsFilterOptionField,
    limit: params.limit,
    offset: params.offset,
  });

  return toStringOptions(response.options);
}

/**
 * Fetch distinct file path values for the file filter dropdown with pagination.
 */
export async function fetchUserAlertFileOptions(
  params: FilterOptionsParams
): Promise<{
  options: string[];
  nextOffset?: number | null;
  hasMore?: boolean;
}> {
  const response = await getUserAlertFilterOptions(apiClient, {
    field:
      USER_ALERTS_FILTERABLE_FIELDS.FILE_PATH as UserAlertsFilterOptionField,
    search: params.search,
    limit: params.limit,
    offset: params.offset,
  });

  return {
    options: toStringOptions(response.options),
    nextOffset: response.pagination.nextOffset ?? null,
    hasMore: response.pagination.hasMore,
  };
}

/**
 * Fetch distinct client-type values for the IDE/Platform filter dropdown.
 */
export async function fetchUserAlertClientTypeOptions(
  params: FilterOptionsParams
): Promise<string[]> {
  const response = await getUserAlertFilterOptions(apiClient, {
    field:
      USER_ALERTS_FILTERABLE_FIELDS.CLIENT_TYPES as UserAlertsFilterOptionField,
    search: params.search,
    limit: params.limit,
    offset: params.offset,
  });

  return toStringOptions(response.options);
}
